import type { ContactCandidateOut, PropertyContactWithDetails } from '@/client';
import type { PropertyContactWithServiceFields } from '@/components/services/model/serviceContacts';

/**
 * Ids for records that do not exist in `property_contacts`, kept negative so one can never be
 * mistaken for — or collide with — a real contact id anywhere downstream.
 */
const asPlaceholderId = (candidateId: number) => -candidateId;

/** Nothing on the wire dates an extracted provider, and the panel needs the field to exist. */
const UNKNOWN_TIMESTAMP = new Date(0).toISOString();

/**
 * A provider Bundle read out of a document, in the shape the rest of the services UI speaks.
 *
 * MOCK SEAM. Extraction currently stops at the review queue: there is no `property_contacts` row
 * behind one of these, so the details panel, connecting, editing and deleting have nothing to act
 * on. Adapting the candidate here is what lets the directory treat every provider alike while the
 * backend catches up — a provider on the page is a provider, and the page should not be the thing
 * that remembers which ones the household typed in.
 *
 * TODO(services/extracted-providers): delete this file once extracted providers land as real
 * property contacts. Until then the identifiers below are placeholders, and any write aimed at one
 * will not find a record.
 */
export const contactFromCandidate = (candidate: ContactCandidateOut, tradeLabel: string, tradeCode: string) => {
	const id = asPlaceholderId(candidate.id);

	const contact: PropertyContactWithServiceFields = {
		id,
		house_id: 0,
		contact_id: id,
		category_id: 0,
		notes: candidate.notes ?? null,
		scope: candidate.scope ?? null,
		is_connected: false,
		account_user_id: null,
		account: null,
		source: candidate.source,
		extraction_run_id: null,
		status: candidate.status,
		created_at: UNKNOWN_TIMESTAMP,
		updated_at: UNKNOWN_TIMESTAMP,
		added_by_user_id: null,
		category: {
			id: 0,
			code: tradeCode,
			label: tradeLabel,
			is_custom: true,
			created_at: UNKNOWN_TIMESTAMP,
			created_by_user_id: null,
		},
		contact: {
			id,
			// A candidate can arrive as a company with nobody named — an invoice gives the company and no
			// person — so the company stands in rather than the panel reading "Unnamed".
			name: candidate.name ?? candidate.company ?? 'Unnamed contact',
			company: candidate.name ? (candidate.company ?? null) : null,
			// Carried through so the detail panel's trust guard reads the same answer the card does.
			party_type: candidate.party_type ?? null,
			email: candidate.email ?? null,
			phone: candidate.phone ?? null,
			address: null,
			website: null,
			user_id: null,
			// Null for every contact created from here on; a fabricated organisation is not rendered.
			organization_id: null,
			created_at: UNKNOWN_TIMESTAMP,
			updated_at: UNKNOWN_TIMESTAMP,
		},
	};

	return contact as PropertyContactWithDetails;
};
